import { useState } from 'react';
import { Prose } from '@/features/onboarding/components/Prose';
import type { FlowActor, FlowStep } from '@/features/onboarding/data/harness';

/** 누가 하는 단계인가를 색으로 가른다. 클래스 이름을 조립하지 않고 표에서 고른다(Tailwind가 조립된 이름을 못 찾는다). */
const ACTORS: Record<FlowActor, { label: string; node: string; selected: string; chip: string }> = {
  requester: {
    label: '🙌 요청자',
    node: 'border-sky-300 bg-sky-50 hover:border-sky-400',
    selected: 'border-sky-500 bg-sky-100 ring-2 ring-sky-200',
    chip: 'border-sky-300 bg-sky-50 text-sky-800',
  },
  auto: {
    label: '🤖 자동',
    node: 'border-slate-300 bg-slate-50 hover:border-slate-400',
    selected: 'border-slate-500 bg-slate-100 ring-2 ring-slate-200',
    chip: 'border-slate-300 bg-slate-50 text-slate-700',
  },
  dev: {
    label: '🧑‍💻 개발팀',
    node: 'border-emerald-300 bg-emerald-50 hover:border-emerald-400',
    selected: 'border-emerald-500 bg-emerald-100 ring-2 ring-emerald-200',
    chip: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  },
  qa: {
    label: '🔍 QA',
    node: 'border-violet-300 bg-violet-50 hover:border-violet-400',
    selected: 'border-violet-500 bg-violet-100 ring-2 ring-violet-200',
    chip: 'border-violet-300 bg-violet-50 text-violet-800',
  },
};

/**
 * 단계를 화살표로 잇는 흐름도 — 넓은 화면에서는 가로 한 줄, 좁으면 세로로 쌓인다.
 * 노드를 누르면 그 단계의 설명이 아래에 열린다. 설명을 노드 안에 다 넣으면 일곱 칸이 글로 덮여 흐름이 안 보인다.
 *
 * `loopBack`은 마지막 단계에서 되돌아가는 단계의 위치다(QA에서 이슈가 나오면 개발로). 넓은 화면에서는 그
 * 구간 아래에 점선 괄호로, 좁은 화면에서는 한 줄 안내로 그린다.
 */
export function FlowDiagram({ steps, loopBack, loopLabel }: { steps: FlowStep[]; loopBack?: number; loopLabel?: string }): React.JSX.Element {
  const [selected, setSelected] = useState(0);
  const step = steps[selected];
  const actors = [...new Set(steps.map((candidate) => candidate.actor).filter((actor): actor is FlowActor => actor !== undefined))];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        {actors.map((actor) => (
          <span key={actor} className={`rounded-full border px-2 py-0.5 font-semibold ${ACTORS[actor].chip}`}>
            {ACTORS[actor].label}
          </span>
        ))}
        <span className="ml-auto text-faint">단계를 누르면 자세한 설명이 열립니다</span>
      </div>

      <ol className="grid grid-cols-1 gap-5 xl:grid-cols-7 xl:gap-4">
        {steps.map((candidate, index) => {
          const actor = ACTORS[candidate.actor ?? 'auto'];
          return (
            <li key={candidate.title} className="relative">
              <button
                type="button"
                onClick={() => setSelected(index)}
                className={`flex h-full w-full flex-col items-start gap-1 rounded-lg border-2 p-2.5 text-left transition-colors ${
                  index === selected ? actor.selected : actor.node
                }`}
              >
                <span className="text-[10.5px] font-bold text-faint">STEP {index + 1}</span>
                <span className="text-[12.5px] leading-snug font-semibold text-ink">{candidate.title}</span>
                {candidate.skill && (
                  <code className="mt-auto rounded bg-white/70 px-1 font-mono text-[10.5px] break-all text-accent">{candidate.skill}</code>
                )}
              </button>
              {index < steps.length - 1 && (
                <>
                  <span aria-hidden className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[14px] text-faint xl:hidden">↓</span>
                  <span aria-hidden className="absolute top-1/2 -right-4 hidden w-4 -translate-y-1/2 text-center text-[14px] text-faint xl:block">→</span>
                </>
              )}
            </li>
          );
        })}
      </ol>

      {loopBack !== undefined && loopLabel && (
        <>
          <div className="hidden xl:grid xl:grid-cols-7 xl:gap-4">
            <div
              className="relative h-5 rounded-b-lg border-x-2 border-b-2 border-dashed border-rose-300"
              style={{ gridColumnStart: loopBack + 1, gridColumnEnd: steps.length + 1 }}
            >
              <span aria-hidden className="absolute -top-2.5 -left-[7px] text-[12px] leading-none text-rose-400">▲</span>
              <span className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 bg-surface px-2 text-[11px] font-semibold whitespace-nowrap text-rose-600">
                ↩ {loopLabel}
              </span>
            </div>
          </div>
          <p className="text-[11.5px] font-semibold text-rose-600 xl:hidden">↩ {loopLabel}</p>
        </>
      )}

      {step && (
        <div className="mt-1 rounded-lg border border-line-soft bg-line-soft/40 p-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <strong className="text-[13px]">
              STEP {selected + 1} · {step.title}
            </strong>
            {step.actor && (
              <span className={`rounded-full border px-1.5 py-0 text-[10.5px] font-semibold ${ACTORS[step.actor].chip}`}>{ACTORS[step.actor].label}</span>
            )}
          </div>
          <p className="mt-1 text-[12.5px] text-muted"><Prose text={step.detail} /></p>
          {step.output && (
            <p className="mt-1 text-[11.5px] text-faint">
              <span className="font-semibold">📎 남는 것</span> <Prose text={step.output} />
            </p>
          )}
        </div>
      )}
    </div>
  );
}
